import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';

import { ICompany, useCompanies } from '../../../hooks/useCompanies';
import { IData } from '../../../types/shared';

// Autocomplete MULTI de EMPRESA que propaga os external_ids (codi_emp) — o filtro WHERE
// do token (multi-empresa: N empresas por token). Molde do EmpresaSelect do Estatuto,
// MAS aqui o onChange devolve os external_id (o EmpresaSelect original descarta esse
// campo). Busca server-side por razão social/CNPJ via useCompanies (?search=).

export interface IEmpresaExternal {
  id: number;
  razao_social: string;
  cnpj?: string;
  external_id: string; // codi_emp — é isto que o token usa (WHERE external_id)
}

interface Props {
  // Empresas já selecionadas (multi). O token lê todas.
  value: IEmpresaExternal[];
  // eslint-disable-next-line no-unused-vars
  onChange: (empresas: IEmpresaExternal[]) => void;
}

interface EmpresaOption {
  value: number;
  label: string;
  empresa: IEmpresaExternal;
}

const formatCnpj = (cnpj?: string): string => {
  if (!cnpj) return '';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const optionLabel = (e: IEmpresaExternal): string => {
  const cnpj = formatCnpj(e.cnpj);
  const base = cnpj ? `${e.razao_social} — ${cnpj}` : e.razao_social;
  return `${base} (emp ${e.external_id})`;
};

export const EmpresaExternalIdSelect: React.FC<Props> = ({ value, onChange }) => {
  const { getCompanies } = useCompanies();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  // Cancela o debounce pendente no unmount (evita setState em componente desmontado).
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const { data, isFetching } = useQuery<IData<ICompany[]>>({
    queryKey: [
      'api-empresa-external-select',
      { search: debounced || undefined, page: 1, size: 20 },
    ],
    queryFn: getCompanies,
    enabled: menuOpen,
  });

  const options: EmpresaOption[] = useMemo(
    () =>
      (data?.data?.items ?? [])
        // só empresas com external_id (sem ele o token não filtra)
        .filter(c => !!c.external_id)
        .map(c => {
          const empresa: IEmpresaExternal = {
            id: c.id,
            razao_social: c.razao_social,
            cnpj: c.cnpj,
            external_id: c.external_id,
          };
          return { value: c.id, label: optionLabel(empresa), empresa };
        }),
    [data],
  );

  return (
    <Select<EmpresaOption, true>
      isMulti
      placeholder="Buscar empresa(s) por razão social ou CNPJ…"
      options={options}
      value={value.map(v => ({ value: v.id, label: optionLabel(v), empresa: v }))}
      inputValue={search}
      onInputChange={onInputChange}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      onChange={opts => onChange(opts ? opts.map(o => o.empresa) : [])}
      isLoading={isFetching}
      isClearable
      // não fecha o menu ao escolher (facilita marcar várias)
      closeMenuOnSelect={false}
      filterOption={null}
      noOptionsMessage={() => (debounced ? 'Nenhuma empresa encontrada' : 'Digite para buscar')}
      loadingMessage={() => 'Buscando…'}
      size="sm"
    />
  );
};
